import { ChevronDown } from 'lucide-react';
import {
  Menu,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
  cn,
} from '@wundo/ui';
import { COLLEAGUE_RELATIONSHIPS } from '@wundo/shared/colleagues.ts';
import type { ColleagueRelationship } from '@/lib/colleagues.ts';
import { m } from '@/paraglide/messages.js';

export interface RelationshipPickerProps {
  name: string;
  value: ColleagueRelationship;
  onChange: (relationship: ColleagueRelationship) => void;
  className?: string;
}

const LABELS: Record<ColleagueRelationship, () => string> = {
  team_member: m.team_relationship_team_member,
  manager: m.team_relationship_manager,
  other: m.team_relationship_other,
};

/**
 * Who a colleague is to the user, as a small pill that opens a short menu. A
 * choice other than the default stands out a little, since it changes how
 * Wundo prepares the conversation.
 */
export function RelationshipPicker({
  name,
  value,
  onChange,
  className,
}: RelationshipPickerProps) {
  return (
    <Menu>
      <MenuTrigger
        aria-label={m.team_relationship_label({ name })}
        className={cn(
          'inline-flex w-fit cursor-pointer items-center gap-0.5 rounded-full py-0.5 pr-1.5 pl-2 text-xs outline-none transition-colors hover:bg-line focus-visible:outline-2 focus-visible:outline-ink-faint data-popup-open:bg-line',
          value === 'team_member'
            ? 'bg-fill font-medium text-ink-muted hover:text-ink'
            : 'bg-type-oneonone-tint font-semibold text-ink',
          className,
        )}
      >
        {LABELS[value]()}
        <ChevronDown aria-hidden='true' className='size-3 opacity-80' />
      </MenuTrigger>
      <MenuContent>
        <MenuRadioGroup
          value={value}
          onValueChange={(next) => {
            const picked = COLLEAGUE_RELATIONSHIPS.find((option) => option === next);
            if (picked) onChange(picked);
          }}
        >
          {COLLEAGUE_RELATIONSHIPS.map((relationship) => (
            <MenuRadioItem key={relationship} value={relationship}>
              {LABELS[relationship]()}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}
